import React, { useState } from 'react';
import { MapPin, Phone, Mail, Globe, Send } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Card } from './ui/card';
import { toast } from 'sonner';
import { brandInfo } from '../mock';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const response = await axios.post(`${BACKEND_URL}/api/contact`, formData);
      
      if (response.status === 200) {
        toast.success("Message Sent!", {
          description: "Thank you for contacting us. We'll respond shortly via email.",
        });

        // Reset form
        setFormData({
          name: '',
          email: '',
          phone: '',
          subject: '',
          message: ''
        });
      }
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error("Send Failed", {
        description: "Please try again or email us directly at info@akiwa.tech",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-black mb-4">Get In Touch</h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Have questions? We'd love to hear from you. Send us a message and we'll respond as soon as possible.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Contact Information */}
          <div className="space-y-8">
            <div>
              <h3 className="text-2xl font-bold text-black mb-6">Contact Information</h3>
              <p className="text-gray-600 mb-8">
                Reach out to us through any of the following channels. Our team is ready to assist you.
              </p>
            </div>

            {/* Contact Cards */}
            <div className="space-y-4">
              <Card className="p-6 border-2 border-gray-200 hover:border-black transition-colors duration-300 group">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-black transition-colors duration-300">
                    <MapPin className="text-black group-hover:text-white transition-colors duration-300" size={20} />
                  </div>
                  <div>
                    <h4 className="font-semibold text-black mb-1">Address</h4>
                    <p className="text-gray-600 text-sm">{brandInfo.address}</p>
                  </div>
                </div>
              </Card>

              <Card className="p-6 border-2 border-gray-200 hover:border-black transition-colors duration-300 group">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-black transition-colors duration-300">
                    <Phone className="text-black group-hover:text-white transition-colors duration-300" size={20} />
                  </div>
                  <div>
                    <h4 className="font-semibold text-black mb-1">Phone</h4>
                    <a href={`tel:${brandInfo.phone}`} className="text-gray-600 text-sm hover:text-black transition-colors">
                      {brandInfo.phone}
                    </a>
                  </div>
                </div>
              </Card>

              <Card className="p-6 border-2 border-gray-200 hover:border-black transition-colors duration-300 group">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-black transition-colors duration-300">
                    <Mail className="text-black group-hover:text-white transition-colors duration-300" size={20} />
                  </div>
                  <div>
                    <h4 className="font-semibold text-black mb-1">Email</h4>
                    <a href={`mailto:${brandInfo.email}`} className="text-gray-600 text-sm hover:text-black transition-colors">
                      {brandInfo.email}
                    </a>
                  </div>
                </div>
              </Card>

              <Card className="p-6 border-2 border-gray-200 hover:border-black transition-colors duration-300 group">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-black transition-colors duration-300">
                    <Globe className="text-black group-hover:text-white transition-colors duration-300" size={20} />
                  </div>
                  <div>
                    <h4 className="font-semibold text-black mb-1">Website</h4>
                    <a href={`https://${brandInfo.website}`} target="_blank" rel="noopener noreferrer" className="text-gray-600 text-sm hover:text-black transition-colors">
                      {brandInfo.website}
                    </a>
                  </div>
                </div>
              </Card>
            </div>

            {/* Map Placeholder */}
            <div className="h-64 bg-gray-200 rounded-lg border-2 border-gray-300 overflow-hidden">
              <iframe
                title="AKIWA Location"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3683.0861779999995!2d88.4119!3d22.6267!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMjLCsDM3JzM2LjEiTiA4OMKwMjQnNDIuOCJF!5e0!3m2!1sen!2sin!4v1234567890"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
              ></iframe>
            </div>
          </div>

          {/* Contact Form */}
          <div>
            <Card className="p-8 border-2 border-gray-200 shadow-lg">
              <h3 className="text-2xl font-bold text-black mb-6">Send Us a Message</h3>
              
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Name */}
                <div className="space-y-2">
                  <Label htmlFor="contact-name" className="text-gray-700 font-medium">Name *</Label>
                  <Input
                    id="contact-name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    placeholder="Your name"
                    className="border-gray-300 focus:border-black focus:ring-black"
                  />
                </div>

                {/* Email */}
                <div className="space-y-2">
                  <Label htmlFor="contact-email" className="text-gray-700 font-medium">Email *</Label>
                  <Input
                    id="contact-email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    placeholder="your.email@example.com"
                    className="border-gray-300 focus:border-black focus:ring-black"
                  />
                </div>

                {/* Phone */}
                <div className="space-y-2">
                  <Label htmlFor="contact-phone" className="text-gray-700 font-medium">Phone</Label>
                  <Input
                    id="contact-phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="Your phone number"
                    className="border-gray-300 focus:border-black focus:ring-black"
                  />
                </div>

                {/* Subject */}
                <div className="space-y-2">
                  <Label htmlFor="contact-subject" className="text-gray-700 font-medium">Subject *</Label>
                  <Input
                    id="contact-subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    required
                    placeholder="How can we help?"
                    className="border-gray-300 focus:border-black focus:ring-black"
                  />
                </div>

                {/* Message */}
                <div className="space-y-2">
                  <Label htmlFor="contact-message" className="text-gray-700 font-medium">Message *</Label>
                  <Textarea
                    id="contact-message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={5}
                    placeholder="Tell us more about your inquiry..."
                    className="border-gray-300 focus:border-black focus:ring-black resize-none"
                  />
                </div>

                {/* Submit Button */}
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-black hover:bg-gray-800 text-white py-6 text-lg transition-all duration-300 hover:scale-105 shadow-lg group disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Send className="mr-2 group-hover:translate-x-1 transition-transform" size={20} />
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                </Button>
              </form>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
